import React, { useCallback, useEffect, useState } from 'react';
import { Segmented, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import NavBadge from '../../components/NavBadge';
import { useTranslation } from 'react-i18next';
import { IconFileInvoice } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import BackButton from '../../components/BackButton';
import EmptyState from '../../components/EmptyState';
import GearLoadingState from '../../components/GearLoadingState';
import ServiceRequestCard from '../../components/ServiceRequestCard';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { useServiceProviderProfile } from '../../contextProviders/ServiceProviderProfileProvider';
import { useIncomingServiceRequestsBadge } from '../../contextProviders/IncomingServiceRequestsProvider';
import { getIncomingServiceRequests, getMyServiceRequests } from '../../services/ServiceRequestService';
import { IServiceRequestSummary } from '../../interfaces/heavyDutyMachinery/IServiceRequest';
import { OPEN_SERVICE_REQUEST_STATUSES } from '../../constants/serviceRequestOptions';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { SURFACE_1_SOFT } from '../../theme/creamyTokens';

const { Title } = Typography;

type Filter = 'open' | 'all';

interface ServiceRequestsProps {
  /**
   * Whose list this is. The two sides share this screen because it is the
   * same list of the same rows -- the server already worked out whose name
   * goes on each one.
   */
  mode: 'mine' | 'incoming';
}

/**
 * Either side's list of quote requests.
 *
 * Defaults to what is still live rather than to everything: a professional
 * opens this to answer what is unanswered, and a customer to see whether
 * anybody has come back to them. The history is one tap away and nobody
 * needs it first.
 *
 * The two sides stay two ROUTES and share one screen, which is worth saying
 * plainly because it looks like a contradiction. Two routes, because a
 * notification about a request that was just sent has to land on the
 * professional's side and nowhere else, and a URL is the only thing that
 * survives a cold start from a push notification. One screen, because a
 * plumber who also hires a digger is both people, and making them find a
 * second menu entry to switch hats is the kind of thing that gets a feature
 * called "missing".
 *
 * So the segmented control below navigates rather than sets state: what side
 * you are on is in the address bar, where it can be linked to, gone back
 * from, and reloaded.
 */
const ServiceRequests: React.FC<ServiceRequestsProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { hasServiceProviderProfile } = useServiceProviderProfile();
  const { unseenCount, setViewingIncomingRequests } = useIncomingServiceRequestsBadge();

  const [requests, setRequests] = useState<IServiceRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('open');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = mode === 'incoming' ? await getIncomingServiceRequests() : await getMyServiceRequests();
      setRequests(rows);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.SERVICE_REQUEST_LOAD_ERROR));
    } finally {
      setLoading(false);
    }
  }, [mode, t]);

  useEffect(() => {
    load();
  }, [load]);

  // The badge clears while the professional's own side is the one on screen,
  // and only then: a customer reading their own requests has not seen what
  // somebody asked THEM for. See IncomingServiceRequestsProvider.
  useEffect(() => {
    if (mode !== 'incoming') return;
    setViewingIncomingRequests(true);
    return () => setViewingIncomingRequests(false);
  }, [mode, setViewingIncomingRequests]);

  // Filtered here rather than re-fetched: the list is short by nature (one
  // page of it), and a round trip to hide three rows is a round trip nobody
  // asked for.
  const visible = filter === 'open'
    ? requests.filter((r) => OPEN_SERVICE_REQUEST_STATUSES.includes(r.status))
    : requests;

  return (
    <div style={{ minHeight: '100vh', background: SURFACE_1_SOFT, paddingBottom: 40 }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: isMobile ? '12px 16px' : '24px 20px' }}>
        <BackButton fallbackPath="/heavy-duty-machinery" />

        <Title level={3} style={{ marginTop: 12, marginBottom: 16 }}>
          {/* One title for both sides now that the switch below says which
              one you are on -- a heading that changes under a control that
              already changed reads like the page reloaded into something
              else. */}
          {translate(t, TRANSLATIONS.SERVICE_REQUESTS_TITLE)}
        </Title>

        {/* Only for somebody who has both hats. A customer with no
            ServiceProviderProfile has nothing behind the other side, and an
            empty tab that can never fill is worse than no tab. */}
        {hasServiceProviderProfile && (
          <Segmented
            block={isMobile}
            value={mode}
            onChange={(value) =>
              navigate(
                value === 'incoming'
                  ? '/heavy-duty-machinery/service-requests/incoming'
                  : '/heavy-duty-machinery/service-requests'
              )
            }
            options={[
              { label: translate(t, TRANSLATIONS.SERVICE_REQUESTS_SIDE_MINE), value: 'mine' },
              {
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {translate(t, TRANSLATIONS.SERVICE_REQUESTS_SIDE_INCOMING)}
                    {unseenCount > 0 && <NavBadge count={unseenCount} />}
                  </span>
                ),
                value: 'incoming',
              },
            ]}
            style={{ marginBottom: 12 }}
          />
        )}

        <Segmented
          block={isMobile}
          value={filter}
          onChange={(value) => setFilter(value as Filter)}
          options={[
            { label: translate(t, TRANSLATIONS.SERVICE_REQUESTS_FILTER_OPEN), value: 'open' },
            { label: translate(t, TRANSLATIONS.SERVICE_REQUESTS_FILTER_ALL), value: 'all' },
          ]}
          style={{ marginBottom: 16 }}
        />

        {loading ? (
          <GearLoadingState />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<IconFileInvoice size={22} />}
            title={translate(t, mode === 'incoming' ? TRANSLATIONS.SERVICE_REQUESTS_EMPTY_INCOMING : TRANSLATIONS.SERVICE_REQUESTS_EMPTY_MINE)}
            description={translate(t, mode === 'incoming' ? TRANSLATIONS.SERVICE_REQUESTS_EMPTY_INCOMING_HINT : TRANSLATIONS.SERVICE_REQUESTS_EMPTY_MINE_HINT)}
          />
        ) : (
          visible.map((request) => (
            <ServiceRequestCard
              key={request.id}
              request={request}
              onOpen={() => navigate(`/heavy-duty-machinery/service-requests/${request.id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ServiceRequests;
